/**
 * LOS pipeline API — wraps the pipeline + single-loan endpoints on the
 * eastview-os backend (loan_and_auth_endpoints.md §3–§6).
 *
 * There are two parallel surfaces; both are server-scoped to the caller (admins
 * w/o a company → all deals; company-scoped users → their originated/assigned
 * deals; everyone else → only deals they're assigned to). The frontend never
 * passes a user/email filter.
 *
 *   - `/underwriting/*` — the **LOS** surface. Per §4 the doc recommends
 *     `/underwriting/underwriting_pipeline` for the pre-close / closing /
 *     post-close boards (sorted by loanNumber desc), and
 *     `/underwriting/loan/{id}` as the primary single-loan read.
 *   - `/loportal/*` — the **sales / LO** surface. `user_pipeline` carries extra
 *     `openConditionsCount` / `openExceptionsCount` badge counts; the rest are
 *     sales-side equivalents of the `/underwriting/*` mutations. The doc says
 *     "Prefer the /underwriting/* versions for the LOS."
 *
 * There is NO per-stage endpoint — Pre-Close / Closing / Post-Close are
 * client-side slices of one pipeline payload (see `constants/pipelineTabs.js`).
 *
 * `getLoan` 403s for a company-scoped user who opened a deal they aren't entitled
 * to — surfaced here as `AccessDeniedError` so callers can show "no access"
 * instead of crashing.
 */
import { losHttp } from "../lib/losHttp";

/** Thrown by `getLoan` on a 403 so callers can show "no access" vs. a crash. */
export class AccessDeniedError extends Error {
  constructor(message = "Access denied") {
    super(message);
    this.name = "AccessDeniedError";
  }
}

const mapAccessDenied = (err) => {
  if (err?.response?.status === 403) {
    throw new AccessDeniedError(err?.response?.data?.detail);
  }
  throw err;
};

export const losPipelineApi = {
  // ── Pipeline reads (each returns an array of loan summary projections) ────────
  // GET /underwriting/underwriting_pipeline — PRIMARY LOS board source (§4).
  // All loans the caller can see, sorted by loanNumber desc.
  getUnderwritingPipeline: () =>
    losHttp
      .get("/underwriting/underwriting_pipeline", {
        logName: "Get Underwriting Pipeline",
      })
      .then((r) => r.data),

  // Lendmarq divergence (see CLAUDE.md #3): the all-loans payload behind the
  // Lendmarq `/loans` pipeline board + its client-side buckets
  // (LendmarqPipelineScreen / lendmarqPipelineBuckets). Same endpoint as
  // getUnderwritingPipeline today; kept as its own method so the pipeline
  // surface has a stable name and its own log label. Do NOT drop this when
  // syncing losPipelineApi.js from Eastview — Eastview has no Lendmarq board.
  getLendmarqPipeline: () =>
    losHttp
      .get("/underwriting/underwriting_pipeline", {
        logName: "Get Lendmarq Pipeline",
      })
      .then((r) => r.data),
  // GET /underwriting/loan_search?q&limit — header Loan Search over BOTH
  // pipelines (frontend_documentation/loan-search-both-pipelines.md, 2026-09-15).
  // Free text, 2+ chars (shorter returns `[]`); every whitespace token must
  // match loan number / sync_loan_id / name / status / originator / borrower /
  // entity / address. Rows are slim and carry `pipeline`
  // ("Underwriting" | "Acquisitions" — the COLLECTION) plus the frontend deep
  // `link` ("/loan/{id}" | "/acquisition/{id}"); route on `link`, never on the
  // loan document's own `pipeline` field. Pass an AbortSignal to cancel a
  // superseded query. Server-scoped: a correspondent sees only its own loans.
  searchLoans: ({ q, limit = 20, signal } = {}) =>
    losHttp
      .get("/underwriting/loan_search", {
        params: { q, limit },
        signal,
        logName: "Loan Search",
      })
      .then((r) => (Array.isArray(r.data) ? r.data : [])),

  // GET /loportal/user_pipeline — sales/LO view; adds openConditionsCount /
  // openExceptionsCount per loan (§4).
  getUserPipeline: () =>
    losHttp.get("/loportal/user_pipeline").then((r) => r.data),

  // GET /underwriting/counterparty_pipeline — investor/counterparty view.
  // ADMIN-ONLY (403 otherwise); includes investorCounterparty (§4).
  getCounterpartyPipeline: () =>
    losHttp.get("/underwriting/counterparty_pipeline").then((r) => r.data),

  // POST /underwriting/search_loan_by_tag/{dealId} { tags: [...] } (§4).
  searchLoanByTag: (dealId, tags) =>
    losHttp
      .post(`/underwriting/search_loan_by_tag/${dealId}`, {
        tags: Array.isArray(tags) ? tags : [tags],
      })
      .then((r) => r.data),

  // ── Single loan (full object) ────────────────────────────────────────────────
  // GET /underwriting/loan/{loanId} — PRIMARY full loan read; stamps
  // lastViewDateTime/lastViewBy (§5). 403 → AccessDeniedError.
  getLoan: (loanId) =>
    losHttp
      .get(`/underwriting/loan/${loanId}`)
      .then((r) => r.data)
      .catch(mapAccessDenied),

  // GET /loan_acquisitions/loan/{loanId} — the acquisition's full read (same
  // shape as the drilldown's, via the acquisitions apiPrefix).
  getAcquisitionLoan: (loanId) =>
    losHttp
      .get(`/loan_acquisitions/loan/${loanId}`)
      .then((r) => r.data)
      .catch(mapAccessDenied),

  // GET /loportal/loan/{loanId} — sales-side equivalent full loan read (§5).
  getLoanLoportal: (loanId) =>
    losHttp
      .get(`/loportal/loan/${loanId}`)
      .then((r) => r.data)
      .catch(mapAccessDenied),

  // ── Mutations (all return the updated FULL deal) ──────────────────────────────
  // POST /underwriting/update_loan_data/{loanId} { ...root fields to $set } (§6).
  updateLoanData: (loanId, fields) =>
    losHttp
      .post(`/underwriting/update_loan_data/${loanId}`, fields)
      .then((r) => r.data),

  // POST /underwriting/update_milestone/{dealId} { loanStatus, subStatus? } (§6).
  // `loanStatus` is required; advances pre-close → closing.
  updateMilestone: (dealId, loanStatus, subStatus) =>
    losHttp
      .post(`/underwriting/update_milestone/${dealId}`, {
        loanStatus,
        ...(subStatus != null ? { subStatus } : {}),
      })
      .then((r) => r.data),

  // POST /underwriting/update_sub_status/{dealId} { subStatus } (§6).
  updateSubStatus: (dealId, subStatus) =>
    losHttp
      .post(`/underwriting/update_sub_status/${dealId}`, { subStatus })
      .then((r) => r.data),

  // POST /underwriting/mark_deal_closed/{dealId} { closingDate } → post-close (§6).
  markDealClosed: (dealId, closingDate) =>
    losHttp
      .post(`/underwriting/mark_deal_closed/${dealId}`, { closingDate })
      .then((r) => r.data),

  // POST /underwriting/mark_deal_dead/{dealId} { dealDeadReason, dealDeadNotes } (§6).
  markDealDead: (dealId, dealDeadReason, dealDeadNotes) =>
    losHttp
      .post(`/underwriting/mark_deal_dead/${dealId}`, {
        dealDeadReason,
        dealDeadNotes,
      })
      .then((r) => r.data),

  // POST /underwriting/archive_loan/{dealId} { reason? } — soft-archive the loan
  // (deletedBorrowers[]-style pattern). Returns the updated loan.
  archiveLoan: (dealId, reason) =>
    losHttp
      .post(`/underwriting/archive_loan/${dealId}`, reason ? { reason } : {})
      .then((r) => r.data),

  // POST /underwriting/restore_loan/{dealId} — un-archive. No body.
  restoreLoan: (dealId) =>
    losHttp.post(`/underwriting/restore_loan/${dealId}`, {}).then((r) => r.data),

  // GET /underwriting/archived_loans — the archived pipeline slice.
  getArchivedLoans: () =>
    losHttp.get(`/underwriting/archived_loans`).then((r) => r.data),

  // POST /underwriting/move_to_acquisitions/{dealId} — preclose → acquisitions.
  moveToAcquisitions: (dealId) =>
    losHttp
      .post(`/underwriting/move_to_acquisitions/${dealId}`, {})
      .then((r) => r.data),

  // POST /loan_acquisitions/move_to_underwriting/{dealId} — acquisitions → preclose.
  moveToUnderwriting: (dealId) =>
    losHttp
      .post(`/loan_acquisitions/move_to_underwriting/${dealId}`, {})
      .then((r) => r.data),

  // POST /underwriting/add_loan_tag/{dealId} { tags: [...] } (§7).
  addLoanTag: (dealId, tags) =>
    losHttp
      .post(`/underwriting/add_loan_tag/${dealId}`, {
        tags: Array.isArray(tags) ? tags : [tags],
      })
      .then((r) => r.data),

  // POST /underwriting/remove_loan_tag/{dealId} { tags: [...] } (§7).
  removeLoanTag: (dealId, tags) =>
    losHttp
      .post(`/underwriting/remove_loan_tag/${dealId}`, {
        tags: Array.isArray(tags) ? tags : [tags],
      })
      .then((r) => r.data),

  // POST /underwriting/create_comment/{dealId} { ...comment fields } (§7).
  createComment: (dealId, comment) =>
    losHttp
      .post(`/underwriting/create_comment/${dealId}`, comment)
      .then((r) => r.data),

  // ── Sales-side (/loportal) equivalents (§6 note) ──────────────────────────────
  // POST /loportal/create_prospect { ...loan fields }
  createProspect: (loan) =>
    losHttp.post("/loportal/create_prospect", loan).then((r) => r.data),

  // POST /loportal/add_subscribed_user/{dealId} (subscribes the current user)
  addSubscribedUser: (dealId) =>
    losHttp
      .post(`/loportal/add_subscribed_user/${dealId}`, {})
      .then((r) => r.data),

  // POST /loportal/remove_subscribed_user/{dealId} { subscribedUser }
  removeSubscribedUser: (dealId, subscribedUser) =>
    losHttp
      .post(`/loportal/remove_subscribed_user/${dealId}`, { subscribedUser })
      .then((r) => r.data),
};

export default losPipelineApi;
